import ArchiveIcon from "@mui/icons-material/Archive";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import RestoreIcon from "@mui/icons-material/Restore";
import TuneIcon from "@mui/icons-material/Tune";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Badge from "@mui/material/Badge";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  formatDate,
  getFormStatusLabel,
  getFormTypeLabel,
} from "../utils/treasuryFormsFormatters";

function StatusChip({ status }) {
  const isArchived = status === "ARCHIVED";
  return (
    <Chip
      label={getFormStatusLabel(status)}
      size="small"
      sx={{
        height: 23,
        borderRadius: "999px",
        fontWeight: 800,
        fontSize: 11,
        bgcolor: isArchived
          ? "rgba(239, 83, 80, 0.18)"
          : "rgba(185, 246, 202, 0.72)",
        color: isArchived ? "error.main" : "primary.900",
      }}
    />
  );
}

const primaryActionSx = {
  width: 30,
  height: 30,
  borderRadius: "10px",
  color: "primary.800",
  bgcolor: "secondary.100",
  "&:hover": { bgcolor: "secondary.200" },
};

export default function PlantillasTable({
  rows = [],
  loading = false,
  error = "",
  onRetry,
  onView,
  onEdit,
  onManage,
  onArchive,
  onRestore,
  processingId = null,
  emptyMessage = "No se encontraron plantillas de formularios.",
}) {
  if (loading && rows.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 6,
          borderRadius: "20px",
          bgcolor: "neutral.50",
          textAlign: "center",
          boxShadow: "none",
        }}
      >
        <Stack alignItems="center" spacing={1.5}>
          <CircularProgress size={32} />
          <Typography
            sx={{ fontSize: 14, fontWeight: 700, color: "primary.900" }}
          >
            Cargando plantillas...
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (error && rows.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "20px",
          bgcolor: "neutral.50",
          boxShadow: "none",
        }}
      >
        <Alert
          severity="error"
          action={
            onRetry ? (
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={onRetry}
                sx={{ fontWeight: 800 }}
              >
                Reintentar
              </Button>
            ) : null
          }
          sx={{ borderRadius: "14px" }}
        >
          {error}
        </Alert>
      </Paper>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: "20px",
        bgcolor: "neutral.50",
        boxShadow: "none",
        border: "1px solid rgba(20, 32, 82, 0.08)",
        overflowX: "auto",
        overflowY: "hidden",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <Table sx={{ minWidth: 720 }} size="small" aria-label="Tabla de plantillas de formularios">
        <TableHead
          sx={{
            bgcolor: "neutral.600",
            "& .MuiTableCell-head": {
              fontWeight: 700,
              fontSize: 13.5,
              color: "logoblanco",
              py: 1.15,
              borderBottom: "none",
            },
          }}
        >
          <TableRow>
            <TableCell>Nombre</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell>Última modificación</TableCell>
            <TableCell align="center" sx={{ width: 170 }}>
              Acciones
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                <Typography
                  sx={{ fontSize: 14, fontWeight: 600, color: "neutral.600" }}
                >
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => {
              const isArchived = row.status === "ARCHIVED";
              const isProcessing = processingId === row.id;

              return (
                <TableRow
                  key={row.id}
                  hover
                  sx={{
                    bgcolor: "rgba(255,255,255,0.72)",
                    "&:hover": { bgcolor: "secondary.50" },
                    "& .MuiTableCell-root": {
                      py: 1.05,
                      borderBottom: "1px solid rgba(20, 32, 82, 0.07)",
                    },
                    "&:last-child td, &:last-child th": { borderBottom: 0 },
                    opacity: isProcessing ? 0.6 : 1,
                    transition: "opacity 150ms ease",
                  }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      fontWeight: 800,
                      fontSize: 13,
                      color: "primary.900",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: 13,
                        color: "primary.900",
                      }}
                    >
                      {row.name}
                    </Typography>
                    {row.fieldsCount !== undefined ? (
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 500,
                          color: "neutral.600",
                        }}
                      >
                        {row.fieldsCount} {row.fieldsCount === 1 ? "campo" : "campos"} · Versión {row.currentVersionNumber || 1}
                      </Typography>
                    ) : null}
                  </TableCell>

                  <TableCell sx={{ fontSize: 12.5, fontWeight: 600, color: "neutral.800" }}>
                    {getFormTypeLabel(row.type)}
                  </TableCell>

                  <TableCell>
                    <StatusChip status={row.status} />
                  </TableCell>

                  <TableCell sx={{ fontSize: 12.5, color: "neutral.700" }}>
                    {formatDate(row.updatedAt)}
                  </TableCell>

                  <TableCell align="center" sx={{ width: 170 }}>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Tooltip title="Ver plantilla">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Ver plantilla ${row.name}`}
                            disabled={isProcessing}
                            onClick={() => onView(row)}
                            sx={primaryActionSx}
                          >
                            <VisibilityIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        </span>
                      </Tooltip>

                      {!isArchived ? (
                        <>
                          <Tooltip title="Editar plantilla">
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`Editar plantilla ${row.name}`}
                                disabled={isProcessing}
                                onClick={() => onEdit(row)}
                                sx={primaryActionSx}
                              >
                                <EditIcon sx={{ fontSize: 20 }} />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip
                            title={
                              Number(row.pendingReviewCount)
                                ? `Gestionar plantilla · ${row.pendingReviewCount} respuestas pendientes de revisión`
                                : "Gestionar plantilla"
                            }
                          >
                            <span>
                              <Badge
                                badgeContent={Number(row.pendingReviewCount) || 0}
                                color="error"
                                max={99}
                                invisible={!Number(row.pendingReviewCount)}
                                overlap="circular"
                              >
                                <IconButton
                                  size="small"
                                  aria-label={`Gestionar plantilla ${row.name}${
                                    Number(row.pendingReviewCount)
                                      ? `, ${row.pendingReviewCount} respuestas pendientes de revisión`
                                      : ""
                                  }`}
                                  disabled={isProcessing}
                                  onClick={() => onManage(row)}
                                  sx={primaryActionSx}
                                >
                                  <TuneIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                              </Badge>
                            </span>
                          </Tooltip>

                          <Tooltip title="Archivar plantilla">
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`Archivar plantilla ${row.name}`}
                                disabled={isProcessing}
                                onClick={() => onArchive(row)}
                                sx={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: "10px",
                                  color: "error.main",
                                  bgcolor: "rgba(239, 83, 80, 0.10)",
                                  "&:hover": {
                                    bgcolor: "rgba(239, 83, 80, 0.18)",
                                  },
                                }}
                              >
                                <ArchiveIcon sx={{ fontSize: 20 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="Restaurar plantilla">
                          <span>
                            <IconButton
                              size="small"
                              aria-label={`Restaurar plantilla ${row.name}`}
                              disabled={isProcessing}
                              onClick={() => onRestore(row)}
                              sx={{
                                width: 30,
                                height: 30,
                                borderRadius: "10px",
                                color: "success.main",
                                bgcolor: "rgba(76, 175, 80, 0.10)",
                                "&:hover": {
                                  bgcolor: "rgba(76, 175, 80, 0.18)",
                                },
                              }}
                            >
                              <RestoreIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
